/**
 * useInfiniteArtworks - Infinite scroll artworks hook
 * 
 * Manages paginated artwork fetching with infinite scroll support.
 * Handles loading states, error handling, and data accumulation.
 * 
 * Features:
 * - Automatic page loading on scroll
 * - Device-aware page sizes (via useDeviceLimit)
 * - Tab/category change resets
 * - Deduplication of artworks
 * - Error retry support
 * 
 * @module hooks/useInfiniteArtworks
 */
"use client";

import { useState, useEffect, useCallback, useRef } from "react";

// ============================================================================
// Types
// ============================================================================

export type ArtworkTab = "destaques" | "novos" | "exclusivos";

export interface Artwork {
  readonly id: string;
  readonly title: string;
  readonly image: string;
  readonly thumbnailUrl: string;
  readonly tags: readonly string[];
  readonly categoryId?: string;
  readonly isFree?: boolean;
  readonly isCollection?: boolean;
  readonly isPremium?: boolean;
  readonly dominantColor: string;
  readonly format: "JPG" | "PNG" | "PSD" | "Canva" | "3D" | "AI";
  readonly fileSize?: number;
  readonly collectionSlug?: string | null;
}

export interface PaginationInfo {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
  readonly pages: number;
  readonly hasMore: boolean;
}

export interface UseInfiniteArtworksOptions {
  /** Current tab selection */
  readonly tab: ArtworkTab;
  /** Optional category filter */
  readonly categoryId?: string | null;
  /** Items per page */
  readonly pageSize: number;
  /** Whether fetching is enabled */
  readonly enabled?: boolean;
}

export interface UseInfiniteArtworksReturn {
  /** Accumulated artworks array */
  readonly artworks: Artwork[];
  /** Whether initial load is in progress */
  readonly loading: boolean;
  /** Whether loading more items */
  readonly loadingMore: boolean;
  /** Whether there are more items to load */
  readonly hasMore: boolean;
  /** Error message if any */
  readonly error: string | null;
  /** Load next page of artworks (append mode) */
  readonly loadMore: () => Promise<void>;
  /** Go to specific page (replace mode) */
  readonly goToPage: (page: number) => Promise<void>;
  /** Reset to first page (on tab/category change) */
  readonly reset: () => void;
  /** Force refetch from page 1 (clears state and fetches) */
  readonly refetch: () => Promise<void>;
  /** Pagination metadata */
  readonly pagination: PaginationInfo;
}

// ============================================================================
// Constants
// ============================================================================

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:4001/api";

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Build API URL based on tab
 */
function buildApiUrl(tab: ArtworkTab, page: number, limit: number, categoryId?: string | null): string {
  const apiBaseUrl = API_URL.replace(/\/$/, "");
  let url: string;

  switch (tab) {
    case "destaques":
      url = `${apiBaseUrl}/artworks/trending?page=${page}&limit=${limit}`;
      break;
    case "exclusivos":
      url = `${apiBaseUrl}/artworks/free?page=${page}&limit=${limit}`;
      break;
    case "novos":
      url = `${apiBaseUrl}/artworks?page=${page}&limit=${limit}&sortBy=uploaded_at&sortOrder=DESC`;
      break;
    default:
      url = `${apiBaseUrl}/artworks/trending?page=${page}&limit=${limit}`;
  }

  if (categoryId) {
    url += `&categoryId=${categoryId}`;
  }

  return url;
}

/**
 * Build asset URL for thumbnails
 */
function buildAssetUrl(path?: string | null): string {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://") || path.startsWith("blob:")) {
    return path;
  }
  
  const assetBaseUrl = API_URL.replace(/\/$/, "").replace(/\/api$/, "");
  let normalizedPath = path.startsWith("/api/") ? path.replace(/^\/api/, "") : path;
  if (!normalizedPath.startsWith("/")) {
    normalizedPath = `/${normalizedPath}`;
  }
  
  return `${assetBaseUrl}${normalizedPath}`;
}

/**
 * Map raw API artwork to typed Artwork
 */
function mapArtwork(raw: any): Artwork {
  const imageUrl = buildAssetUrl(raw.thumbnailUrl ?? raw.thumbnailPath);
  
  const formatMap: Record<string, Artwork["format"]> = {
    "jpg": "JPG",
    "png": "PNG",
    "psd": "PSD"
  };
  const format = raw.fileFormat 
    ? (formatMap[raw.fileFormat.toLowerCase()] || "JPG")
    : "JPG";

  return {
    id: raw.id,
    title: raw.title,
    image: imageUrl,
    thumbnailUrl: imageUrl,
    tags: raw.tags || [],
    categoryId: raw.categoryId,
    isFree: raw.isFree,
    isCollection: raw.isCollection,
    isPremium: !raw.isFree,
    dominantColor: "#6B46C1",
    format,
    fileSize: raw.fileSize ? parseInt(raw.fileSize) : undefined,
    collectionSlug: raw.collectionSlug || raw.collection_slug || null
  };
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for infinite scroll artwork loading
 * 
 * @example
 * ```tsx
 * const { pageSize } = useDeviceLimit();
 * const { 
 *   artworks, 
 *   loading, 
 *   loadingMore, 
 *   hasMore, 
 *   loadMore 
 * } = useInfiniteArtworks({
 *   tab: "destaques",
 *   categoryId: selectedCategoryId,
 *   pageSize
 * });
 * ```
 */
export function useInfiniteArtworks({
  tab,
  categoryId = null,
  pageSize,
  enabled = true
}: UseInfiniteArtworksOptions): UseInfiniteArtworksReturn {
  // ============================================================================
  // State
  // ============================================================================
  
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationInfo>({
    page: 0,
    limit: pageSize,
    total: 0,
    pages: 0,
    hasMore: true
  });

  // Refs for request deduplication
  const isFetchingRef = useRef(false);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const lastFetchParamsRef = useRef<string>("");

  // ============================================================================
  // Fetch Function
  // ============================================================================

  /**
   * Fetch artworks for a specific page
   */
  const fetchPage = useCallback(async (page: number, isInitial: boolean = false) => {
    // Prevent duplicate requests
    if (isFetchingRef.current) {
      console.log("[useInfiniteArtworks] Skipping duplicate fetch");
      return;
    }

    const fetchParams = `${tab}-${categoryId}-${page}-${pageSize}`;
    if (fetchParams === lastFetchParamsRef.current && !isInitial) {
      console.log("[useInfiniteArtworks] Skipping same params fetch");
      return;
    }

    isFetchingRef.current = true;
    lastFetchParamsRef.current = fetchParams;

    if (isInitial) {
      setLoading(true);
      setError(null);
    } else {
      setLoadingMore(true);
    }

    try {
      const url = buildApiUrl(tab, page, pageSize, categoryId);
      console.log(`[useInfiniteArtworks] Fetching page ${page}:`, url);

      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch artworks`);
      }

      const result = await response.json();
      
      if (!result.success) {
        throw new Error(result.error || "API returned error");
      }

      const rawArtworks = result.data || [];
      const paginationData = result.pagination || {
        page,
        limit: pageSize,
        total: rawArtworks.length,
        pages: 1,
        hasMore: false
      };

      // Map and deduplicate artworks
      const newArtworks: Artwork[] = [];
      for (const raw of rawArtworks) {
        if (!seenIdsRef.current.has(raw.id)) {
          seenIdsRef.current.add(raw.id);
          newArtworks.push(mapArtwork(raw));
        }
      }

      console.log(`[useInfiniteArtworks] Fetched ${newArtworks.length} new artworks (${rawArtworks.length - newArtworks.length} duplicates filtered)`);

      // Update state
      if (isInitial) {
        setArtworks(newArtworks);
      } else {
        setArtworks(prev => [...prev, ...newArtworks]);
      }

      setPagination({
        page: paginationData.page,
        limit: paginationData.limit,
        total: paginationData.total,
        pages: paginationData.pages,
        hasMore: paginationData.hasMore ?? (paginationData.page < paginationData.pages)
      });

    } catch (err) {
      const message = err instanceof Error ? err.message : "Erro ao carregar artes";
      console.error("[useInfiniteArtworks] Fetch error:", message);
      setError(message);
    } finally {
      isFetchingRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, [tab, categoryId, pageSize]);

  // ============================================================================
  // Load More
  // ============================================================================

  /**
   * Load the next page of artworks (append mode)
   */
  const loadMore = useCallback(async () => {
    if (loadingMore || loading || !pagination.hasMore) {
      console.log("[useInfiniteArtworks] loadMore skipped:", { 
        loadingMore, 
        loading, 
        hasMore: pagination.hasMore 
      });
      return;
    }

    const nextPage = pagination.page + 1;
    console.log(`[useInfiniteArtworks] Loading more (page ${nextPage})`);
    await fetchPage(nextPage, false);
  }, [loadingMore, loading, pagination.hasMore, pagination.page, fetchPage]);

  // ============================================================================
  // Go To Page
  // ============================================================================

  /**
   * Go to a specific page (replace mode - clears previous artworks)
   */
  const goToPage = useCallback(async (page: number) => {
    if (loading || loadingMore) {
      console.log("[useInfiniteArtworks] goToPage skipped: already loading");
      return;
    }

    if (page < 1 || page > pagination.pages) {
      console.log("[useInfiniteArtworks] goToPage skipped: invalid page", { page, totalPages: pagination.pages });
      return;
    }

    console.log(`[useInfiniteArtworks] Going to page ${page}`);
    
    // Reset state for new page
    setArtworks([]);
    seenIdsRef.current.clear();
    lastFetchParamsRef.current = "";
    
    // Fetch the specific page
    await fetchPage(page, true);
  }, [loading, loadingMore, pagination.pages, fetchPage]);

  // ============================================================================
  // Reset
  // ============================================================================

  /**
   * Reset to first page (called on tab/category change)
   */
  const reset = useCallback(() => {
    console.log("[useInfiniteArtworks] Resetting state");
    setArtworks([]);
    setPagination({
      page: 0,
      limit: pageSize,
      total: 0,
      pages: 0,
      hasMore: true
    });
    seenIdsRef.current.clear();
    lastFetchParamsRef.current = "";
    isFetchingRef.current = false;
  }, [pageSize]);

  /**
   * Force refetch from page 1 (clears state and fetches immediately)
   */
  const refetch = useCallback(async () => {
    console.log("[useInfiniteArtworks] Refetching from page 1");
    // Clear state first
    setArtworks([]);
    setPagination({
      page: 0,
      limit: pageSize,
      total: 0,
      pages: 0,
      hasMore: true
    });
    seenIdsRef.current.clear();
    lastFetchParamsRef.current = "";
    isFetchingRef.current = false;
    // Then fetch page 1
    await fetchPage(1, true);
  }, [pageSize, fetchPage]);

  // ============================================================================
  // Effects
  // ============================================================================

  /**
   * Fetch initial page on mount and when tab/category changes
   */
  useEffect(() => {
    if (!enabled) return;

    // Reset state for new search params
    setArtworks([]);
    seenIdsRef.current.clear();
    lastFetchParamsRef.current = "";
    
    setPagination({
      page: 0,
      limit: pageSize,
      total: 0,
      pages: 0,
      hasMore: true
    });

    // Fetch first page
    fetchPage(1, true);
  }, [tab, categoryId, pageSize, enabled, fetchPage]);

  // ============================================================================
  // Return
  // ============================================================================

  return {
    artworks,
    loading,
    loadingMore,
    hasMore: pagination.hasMore,
    error,
    loadMore,
    goToPage,
    reset,
    refetch,
    pagination
  };
}

export default useInfiniteArtworks;
